// AdminDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import './AdminDashboard.css';

const AdminDashboard = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState('week');
  const [accessCode, setAccessCode] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [rawUserStats, setRawUserStats] = useState(null);

  // Color scheme
  const THEME = {
    primary: '#4361EE',
    secondary: '#3A0CA3',
    success: '#4CC9F0',
    warning: '#F72585',
    light: '#F8F9FA',
    dark: '#212529',
    background: '#FFFFFF',
    chartColors: ['#4361EE', '#F72585', '#4CC9F0', '#7209B7', '#3A0CA3', '#4895EF', '#560BAD']
  };

  // Authorization function
  const authorize = () => {
    // In production, you would validate this against a secure backend
    if (accessCode === '123') {
      setIsAuthorized(true);
      localStorage.setItem('techGuyGregAdminToken', Date.now().toString()); // Simple token
      fetchStats();
    } else {
      // Animate the input to show error
      const input = document.getElementById('access-code-input');
      input.classList.add('error-shake');
      setTimeout(() => input.classList.remove('error-shake'), 500);
    }
  };

  // Check authorization on component mount
  useEffect(() => {
    const token = localStorage.getItem('techGuyGregAdminToken');
    if (token) {
      // In production, validate token with backend
      setIsAuthorized(true);
      fetchStats();
    } else {
      setLoading(false);
    }

    // Set up keyboard shortcut for login
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && !isAuthorized) {
        authorize();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [accessCode]);

  // Fetch user statistics from localStorage
  const fetchRealUserStats = () => {
    try {
      // Get real user stats from localStorage
      const userStatsJson = localStorage.getItem('userStats');
      console.log('Raw user stats from localStorage:', userStatsJson);
      
      if (!userStatsJson) {
        console.log('No real user stats found in localStorage');
        return null;
      }
      
      // Parse the stats
      const userStats = JSON.parse(userStatsJson);
      console.log('Parsed user stats:', userStats);
      
      return userStats;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return null;
    }
  };

  // Process stats for the selected time range
  const processUserStatsForTimeRange = (rawStats, range) => {
    if (!rawStats) return null;
    
    const now = new Date();
    let startDate = new Date();
    
    // Determine start date based on range
    switch (range) {
      case 'day':
        startDate.setDate(now.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'all':
      default:
        startDate = new Date(0); // Beginning of time
        break;
    }
    
    const startTimestamp = startDate.getTime();
    
    // Filter data based on the time range
    const filteredDailyActivity = {};
    let totalCorrect = 0;
    let totalIncorrect = 0;
    let totalSkipped = 0;
    let rizzPoints = rawStats.totalRizzPoints || 0;
    
    // Process daily activity
    Object.entries(rawStats.dailyActivity || {}).forEach(([date, data]) => {
      const dateTimestamp = new Date(date).getTime();
      if (dateTimestamp >= startTimestamp) {
        filteredDailyActivity[date] = data;
        
        // Count totals
        if (data.correct) totalCorrect += data.correct;
        if (data.incorrect) totalIncorrect += data.incorrect;
        if (data.skipped) totalSkipped += data.skipped;
      }
    });
    
    // Process topic distribution
    const topicDistribution = { ...rawStats.topicsDistribution };
    
    // Calculate total questions for this time period
    const totalQuestions = totalCorrect + totalIncorrect + totalSkipped;
    
    // Format dates to show on charts based on the range
    const formattedDailyActivity = {};
    Object.entries(filteredDailyActivity).forEach(([date, data]) => {
      const formattedDate = formatShortDate(date, range);
      formattedDailyActivity[formattedDate] = formattedDailyActivity[formattedDate] || { correct: 0, incorrect: 0, skipped: 0 };
      formattedDailyActivity[formattedDate].correct += data.correct || 0;
      formattedDailyActivity[formattedDate].incorrect += data.incorrect || 0;
      formattedDailyActivity[formattedDate].skipped += data.skipped || 0;
    });
    
    // Return the processed data
    return {
      dailyActivity: formattedDailyActivity,
      topicDistribution,
      totalQuestions,
      totalCorrect,
      totalIncorrect,
      totalSkipped,
      rizzPoints,
      averageResponseTime: rawStats.averageResponseTime || 0,
      maxStreak: rawStats.maxStreak || 0
    };
  };
  
  // Helper to format date based on time range
  const formatShortDate = (dateStr, range) => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    
    if (range === 'day') {
      return date.getHours() + ':00';
    } else if (range === 'week') {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return dayNames[date.getDay()];
    } else if (range === 'month') {
      return `${date.getMonth() + 1}/${date.getDate()}`;
    } else { // all time
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return monthNames[date.getMonth()];
    }
  };

  // Fetch statistics data
  const fetchStats = async () => {
    setLoading(true);
    try {
      // Try to get real user stats from localStorage
      const realUserStats = fetchRealUserStats();
      
      if (realUserStats) {
        // Process the real stats for the selected time range
        const processedStats = processUserStatsForTimeRange(realUserStats, timeRange);
        
        if (processedStats) {
          setStats(processedStats);
          setLoading(false);
          return;
        }
      }
      
      // Fallback to mock data if no real stats available
      console.log("No real user stats available, using mock data");
      setTimeout(() => {
        setStats(generateMockData(timeRange));
        setLoading(false);
      }, 800);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  // Refresh data when time range changes
  useEffect(() => {
    if (isAuthorized) {
      fetchStats();
    }
  }, [timeRange, isAuthorized]);

  // Handle logging out
  const handleLogout = () => {
    localStorage.removeItem('techGuyGregAdminToken');
    setIsAuthorized(false);
    setStats(null);
  };

  // Refresh stats
  const handleRefresh = () => {
    fetchStats();
  };

  // Function to generate realistic mock data based on time range
  const generateMockData = (range) => {
    // Create different data patterns based on selected time range
    let dailyActivity = [];
    let userGrowth = [];
    const today = new Date();

    if (range === 'day') {
      // Last 24 hours - hourly data
      for (let i = 0; i < 24; i++) {
        const hour = new Date(today);
        hour.setHours(today.getHours() - 23 + i);
        const hourLabel = hour.getHours() + ':00';

        dailyActivity.push({
          time: hourLabel,
          quizzes: Math.floor(15 + Math.random() * 35),
          users: Math.floor(5 + Math.random() * 15)
        });
      }

      // User growth throughout the day
      let cumulativeUsers = 380;
      for (let i = 0; i < 24; i++) {
        const hour = new Date(today);
        hour.setHours(today.getHours() - 23 + i);
        const hourLabel = hour.getHours() + ':00';

        const newUsers = Math.floor(Math.random() * 10);
        cumulativeUsers += newUsers;

        userGrowth.push({
          time: hourLabel,
          total: cumulativeUsers,
          new: newUsers
        });
      }
    } else if (range === 'week') {
      // Last 7 days - daily data
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      for (let i = 0; i < 7; i++) {
        const day = new Date(today);
        day.setDate(today.getDate() - 6 + i);
        const dayLabel = dayNames[day.getDay()];

        dailyActivity.push({
          time: dayLabel,
          quizzes: Math.floor(100 + Math.random() * 150),
          users: Math.floor(30 + Math.random() * 90)
        });
      }

      // User growth throughout the week
      let cumulativeUsers = 350;
      for (let i = 0; i < 7; i++) {
        const day = new Date(today);
        day.setDate(today.getDate() - 6 + i);
        const dayLabel = dayNames[day.getDay()];

        const newUsers = Math.floor(10 + Math.random() * 40);
        cumulativeUsers += newUsers;

        userGrowth.push({
          time: dayLabel,
          total: cumulativeUsers,
          new: newUsers
        });
      }
    } else if (range === 'month') {
      // Last 30 days - show every 3 days
      for (let i = 0; i < 10; i++) {
        const day = new Date(today);
        day.setDate(today.getDate() - 27 + i * 3);
        const dayLabel = `${day.getMonth() + 1}/${day.getDate()}`;

        dailyActivity.push({
          time: dayLabel,
          quizzes: Math.floor(300 + Math.random() * 500),
          users: Math.floor(100 + Math.random() * 250)
        });
      }

      // User growth throughout the month
      let cumulativeUsers = 300;
      for (let i = 0; i < 10; i++) {
        const day = new Date(today);
        day.setDate(today.getDate() - 27 + i * 3);
        const dayLabel = `${day.getMonth() + 1}/${day.getDate()}`;

        const newUsers = Math.floor(30 + Math.random() * 120);
        cumulativeUsers += newUsers;

        userGrowth.push({
          time: dayLabel,
          total: cumulativeUsers,
          new: newUsers
        });
      }
    } else { // all time
      // Last 6 months - monthly data
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      for (let i = 0; i < 6; i++) {
        const month = new Date(today);
        month.setMonth(today.getMonth() - 5 + i);
        const monthLabel = monthNames[month.getMonth()];

        dailyActivity.push({
          time: monthLabel,
          quizzes: Math.floor(1500 + Math.random() * 3000),
          users: Math.floor(500 + Math.random() * 1200)
        });
      }

      // User growth over months
      let cumulativeUsers = 0;
      for (let i = 0; i < 6; i++) {
        const month = new Date(today);
        month.setMonth(today.getMonth() - 5 + i);
        const monthLabel = monthNames[month.getMonth()];

        const newUsers = Math.floor(200 + Math.random() * 500);
        cumulativeUsers += newUsers;

        userGrowth.push({
          time: monthLabel,
          total: cumulativeUsers,
          new: newUsers
        });
      }
    }

    // Topic distribution - relatively stable across time ranges
    const topicDistribution = [
      { name: 'LeetCode', value: 35 + Math.floor(Math.random() * 10) },
      { name: 'DSA', value: 25 + Math.floor(Math.random() * 8) },
      { name: 'Networking', value: 15 + Math.floor(Math.random() * 6) },
      { name: 'System Design', value: 12 + Math.floor(Math.random() * 5) },
      { name: 'OOP', value: 10 + Math.floor(Math.random() * 5) },
      { name: 'OS', value: 8 + Math.floor(Math.random() * 4) }
    ];

    // Correct vs wrong ratio - relatively stable
    const correctRatio = [
      { name: 'Correct', value: 65 + Math.floor(Math.random() * 10) },
      { name: 'Wrong', value: 35 - Math.floor(Math.random() * 10) }
    ];

    // Question difficulty distribution
    const difficultyDistribution = [
      { name: 'Easy', count: 40 + Math.floor(Math.random() * 15) },
      { name: 'Medium', count: 35 + Math.floor(Math.random() * 15) },
      { name: 'Hard', count: 25 + Math.floor(Math.random() * 10) }
    ];

    // User retention data
    const retentionData = [
      { days: '1 Day', retention: 75 + Math.floor(Math.random() * 15) },
      { days: '7 Days', retention: 55 + Math.floor(Math.random() * 15) },
      { days: '30 Days', retention: 40 + Math.floor(Math.random() * 10) },
      { days: '90 Days', retention: 30 + Math.floor(Math.random() * 8) }
    ];

    // Top performing questions
    const topQuestions = [
      { id: 'Q1', question: 'Explain time complexity of quicksort', score: 95 + Math.floor(Math.random() * 5) },
      { id: 'Q2', question: 'Implement a binary search tree', score: 92 + Math.floor(Math.random() * 5) },
      { id: 'Q3', question: 'Describe TCP/IP protocol stack', score: 90 + Math.floor(Math.random() * 5) },
      { id: 'Q4', question: 'Balance a binary search tree', score: 88 + Math.floor(Math.random() * 5) },
      { id: 'Q5', question: 'Explain database indexing', score: 85 + Math.floor(Math.random() * 5) }
    ];

    // Calculate some summary metrics
    const totalUsers = userGrowth[userGrowth.length - 1].total;
    const activeUsers = Math.floor(totalUsers * (0.3 + Math.random() * 0.2)); // 30-50% of total users
    const questionsAnswered = dailyActivity.reduce((sum, day) => sum + day.quizzes, 0);
    const averageStreak = (2 + Math.random() * 2).toFixed(1);
    const conversionRate = (15 + Math.random() * 10).toFixed(1);

    return {
      dailyActivity,
      userGrowth,
      topicDistribution,
      correctRatio,
      difficultyDistribution,
      retentionData,
      topQuestions,
      totalUsers,
      activeUsers,
      questionsAnswered,
      averageStreak,
      conversionRate
    };
  };

  // Calculate percentage change for key metrics
  const getChangePercentage = (current, previous) => {
    if (!previous) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Calculate metric changes
  const metricChanges = useMemo(() => {
    if (!stats) return {};

    // For real stats, we can't easily calculate period-over-period changes
    // without more historical data, so we'll return static values for now
    return {
      questionsChange: '+5.2',
      usersChange: '+8.4',
      streakChange: '+12.0',
      conversionChange: '-2.1'
    };
  }, [stats]);

  // Render loading state
  if (loading) {
    return (
      <div className="admin-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  // Render login screen if not authorized
  if (!isAuthorized) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h1>Admin Dashboard</h1>
          <p className="login-subtitle">Please enter the access code to continue</p>
          
          <div className="login-form">
            <input
              id="access-code-input"
              type="password"
              placeholder="Access Code"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              className="login-input"
            />
            <button onClick={authorize} className="login-button">
              Log In
            </button>
          </div>
          
          <p className="login-help">For demo purposes, use access code: 123</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-title">
          <h1>Admin Dashboard</h1>
          <p className="admin-subtitle">
            Analytics & Performance Monitoring
            <span className="time-range-indicator">
              | {timeRange === 'day' ? 'Last 24 Hours' : 
                 timeRange === 'week' ? 'Last 7 Days' : 
                 timeRange === 'month' ? 'Last 30 Days' : 'All Time'}
            </span>
          </p>
        </div>
        
        <div className="admin-controls">
          <div className="time-range-selector">
            <button 
              className={timeRange === 'day' ? 'active' : ''}
              onClick={() => setTimeRange('day')}
            >
              Day
            </button>
            <button 
              className={timeRange === 'week' ? 'active' : ''}
              onClick={() => setTimeRange('week')}
            >
              Week
            </button>
            <button 
              className={timeRange === 'month' ? 'active' : ''}
              onClick={() => setTimeRange('month')}
            >
              Month
            </button>
            <button 
              className={timeRange === 'all' ? 'active' : ''}
              onClick={() => setTimeRange('all')}
            >
              All
            </button>
          </div>
          
          <div className="admin-actions">
            <button className="refresh-button" onClick={handleRefresh}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6"></path>
                <path d="M1 20v-6h6"></path>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
              </svg>
              Refresh
            </button>
            <button className="logout-button" onClick={handleLogout}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button 
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={activeTab === 'content' ? 'active' : ''}
          onClick={() => setActiveTab('content')}
        >
          Content
        </button>
        <button 
          className={activeTab === 'performance' ? 'active' : ''}
          onClick={() => setActiveTab('performance')}
        >
          Performance
        </button>
      </div>

      {/* Main Content */}
      <div className="admin-content">
        {activeTab === 'overview' && (
          <>
            {/* Key Metrics Row */}
            <div className="metrics-row">
              <div className="metric-card">
                <div className="metric-title">Questions</div>
                <div className="metric-value">{stats?.totalQuestions || 0}</div>
                <div className="metric-subtitle">Total questions answered</div>
                {stats?.changePercentage?.questions && (
                  <div className={`metric-change ${stats.changePercentage.questions > 0 ? 'positive' : 'negative'}`}>
                    {stats.changePercentage.questions > 0 ? '+' : ''}{stats.changePercentage.questions}%
                  </div>
                )}
              </div>
              
              <div className="metric-card">
                <div className="metric-title">Accuracy</div>
                <div className="metric-value">
                  {stats?.totalCorrect && stats?.totalQuestions 
                    ? Math.round((stats.totalCorrect / stats.totalQuestions) * 100) 
                    : 0}%
                </div>
                <div className="metric-subtitle">Correct answer rate</div>
                {stats?.changePercentage?.accuracy && (
                  <div className={`metric-change ${stats.changePercentage.accuracy > 0 ? 'positive' : 'negative'}`}>
                    {stats.changePercentage.accuracy > 0 ? '+' : ''}{stats.changePercentage.accuracy}%
                  </div>
                )}
              </div>
              
              <div className="metric-card">
                <div className="metric-title">Response Time</div>
                <div className="metric-value">{stats?.averageResponseTime?.toFixed(1) || 0}s</div>
                <div className="metric-subtitle">Average answer time</div>
                {stats?.changePercentage?.responseTime && (
                  <div className={`metric-change ${stats.changePercentage.responseTime < 0 ? 'positive' : 'negative'}`}>
                    {stats.changePercentage.responseTime < 0 ? '' : '+'}{stats.changePercentage.responseTime}%
                  </div>
                )}
              </div>
              
              <div className="metric-card">
                <div className="metric-title">Rizz Points</div>
                <div className="metric-value">{stats?.rizzPoints || 0}</div>
                <div className="metric-subtitle">Total points earned</div>
                {stats?.changePercentage?.rizzPoints && (
                  <div className={`metric-change ${stats.changePercentage.rizzPoints > 0 ? 'positive' : 'negative'}`}>
                    {stats.changePercentage.rizzPoints > 0 ? '+' : ''}{stats.changePercentage.rizzPoints}%
                  </div>
                )}
              </div>
            </div>

            {/* Charts Row 1 */}
            <div className="charts-row">
              {/* Daily Activity Chart */}
              <div className="chart-card wide">
                <h3 className="chart-title">Daily Activity</h3>
                <div className="chart-container">
                  {stats.dailyActivity && stats.dailyActivity.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.dailyActivity} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="correct" stackId="a" name="Correct" fill={THEME.success} />
                        <Bar dataKey="incorrect" stackId="a" name="Incorrect" fill={THEME.warning} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="no-data">No activity data available</div>
                  )}
                </div>
              </div>
            </div>

            {/* Charts Row 2 */}
            <div className="charts-row">
              {/* Topic Distribution */}
              <div className="chart-card">
                <h3 className="chart-title">Topics Distribution</h3>
                <div className="chart-container">
                  {stats.topicDistribution && stats.topicDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={stats.topicDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          nameKey="name"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {stats.topicDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={THEME.chartColors[index % THEME.chartColors.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="no-data">No topic data available</div>
                  )}
                </div>
              </div>

              {/* Correct vs Wrong Ratio */}
              <div className="chart-card">
                <h3 className="chart-title">Answer Accuracy</h3>
                <div className="chart-container">
                  {stats.correctRatio && stats.correctRatio.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={stats.correctRatio}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          nameKey="name"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          <Cell key="cell-correct" fill={THEME.success} />
                          <Cell key="cell-wrong" fill={THEME.warning} />
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="no-data">No answer data available</div>
                  )}
                </div>
              </div>
            </div>

            {/* Progress Chart */}
            <div className="charts-row">
              <div className="chart-card wide">
                <h3 className="chart-title">User Progress Over Time</h3>
                <div className="chart-container">
                  {stats.userGrowth && stats.userGrowth.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={stats.userGrowth} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                        <Tooltip />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="total" 
                          name="Total Questions" 
                          stroke={THEME.primary} 
                          activeDot={{ r: 8 }} 
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="correct" 
                          name="Correct Answers" 
                          stroke={THEME.success} 
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="accuracy" 
                          name="Accuracy %" 
                          stroke={THEME.secondary} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="no-data">No progress data available</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Other tabs content would go here */}
      </div>
    </div>
  );
};

export default AdminDashboard;
